import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlContainer,
  FormGroup,
  FormControl,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { GpuFormFieldAppearance, GpuFormFieldModule } from '@geneplanet/ngx-ui/src/lib/form-field';
import { HabitsQuestion } from 'src/app/analyses/interfaces/analyses.types';
import { AnswerBase } from '../../interfaces/answer-base.interface';
import { AnswerDict } from '../../interfaces/answer-dict.interface';
import { TranslateModule } from '@ngx-translate/core';
import { controlContainerProvider } from '../../constants/control-container-provider';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { removeFormControlError } from 'src/app/shared/util/util';
import { AnswerDto } from '../../interfaces/answer-dto.interface';
import { AnalyticsService } from '@geneplanet/ngx-utils/src/lib/analytics';

@UntilDestroy()
@Component({
  selector: 'app-time-dropdown',
  standalone: true,
  imports: [
    CommonModule,
    GpuFormFieldModule,
    ReactiveFormsModule,
    TranslateModule,
  ],
  templateUrl: './time-dropdown.component.html',
  styleUrls: ['./time-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [controlContainerProvider],
})
export class TimeDropdownComponent implements AnswerBase, OnDestroy {
  public question: HabitsQuestion;
  public formFieldAppearance: GpuFormFieldAppearance;
  public answer: AnswerDto;

  protected sleepingHours = Array.from({ length: 25 }, (_, index) => ({
    label: String(index).padStart(2, '0'),
    value: index,
  }));

  protected sleepingMinutes = [
    { label: '00', value: 0 },
    { label: '30', value: 0.5 },
  ];

  private parentContainer = inject(ControlContainer);
  private analyticsService = inject(AnalyticsService);

  get form() {
    return this.parentContainer.control as FormGroup;
  }

  public init(): void {
    this.form.addControl('hours', new FormControl('', Validators.required));
    this.form.addControl('minutes', new FormControl('', Validators.required));

    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      const { hours, minutes } = value;
      if (hours === 24 && minutes === 0.5) {
        this.form.get('hours').setErrors({ invalidTime: true });
      } else {
        removeFormControlError(this.form.get('hours'), 'invalidTime');
      }
    });
  }

  public onSubmit(): AnswerDict {
    const { hours, minutes } = this.form.getRawValue();
    return {
      [this.question.id]: [
        { id: this.question.answers[0].id, value: hours + minutes },
      ],
    };
  }

  ngOnDestroy(): void {
    this.form.removeControl('hours');
    this.form.removeControl('minutes');
  }

  protected track(name: string, value: number): void {
    this.analyticsService.trackFormValue(
      'analyses-add',
      name,
      {
        id: this.question.answers[0].id,
        value: value === 0.5 ? '30' : String(value),
      },
      '1bb8d5e8-e149-43c3-a659-83f75c82ba62',
      this.question.id
    );
  }
}
